/* Starting a bulk binding change — the act the Bulk changes tab could only describe.
 *
 * **Laid out as the design draws it: one card.** Its heading and a Choose bindings control; once
 * open, the change as a two-way switch, the criteria in one row, a live match line with the ceiling
 * beside it, the candidate rows, four figures that separate what will change from what will be
 * skipped, the chunk size, and the one button that starts anything.
 *
 * **Two properties carry it, and both are the platform's rather than the design's.**
 *
 * `willChange` vs `willSkip` — a binding already in the requested state is skipped by the run and
 * left alone by a later revert, so a confirmation promising the selected total over-promises: "412
 * selected" where 374 move. The run's own report makes that split afterwards; making it here is
 * what lets somebody choose differently while they still can.
 *
 * The 5,000 ceiling is shown *before* submission, because the API refuses the whole request over it
 * rather than truncating. A refusal discovered after submitting would be the interface withholding
 * something it already knew — and truncating silently would be worse: a run over the bindings
 * somebody did not choose.
 *
 * **The candidate listing is capped at 500 by the route**, which is below the 5,000 a run may carry.
 * The criteria row says so, always, rather than only once the cap is hit: a select-all over a
 * truncated read would promise a selection the page cannot see.
 */

import { useQueries } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { EDIT_BINDINGS_PERMISSION, can, useIdentity } from '@/entities/session';
import { ruleVersionsQuery, useRules } from '@/features/rules';
import { Alert } from '@/shared/ui/Alert';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { CANDIDATE_PAGE, useBindingCandidates, useRequestBulkRun } from '../api/bulk';
import {
  DEFAULT_CHUNK,
  SELECTION_CEILING,
  actionEvent,
  chunkCount,
  matchesFilters,
  meterTone,
  selectionFacts,
  submitBlock,
} from '../model/presentation';
import type { BulkAction, CandidateFilters } from '../model/presentation';
import type { Rule, RuleBinding, Severity } from '@/shared/api/generated/rules';

/** How many distinct rules' version lists this screen will fetch, to name `rule_version_id`.
 *
 * The same bound `DatasetDetail.tsx` uses for the identical join. A picker over up to 500
 * candidate bindings is realistically bound to a handful of rules — this is a ceiling against a
 * pathological tenant, not a number expected to bite in practice; past it a row shows no version
 * rather than a fan-out with no limit. */
const MAX_RULE_VERSION_LOOKUPS = 12;
const RULE_VERSION_STALE_MS = 5 * 60 * 1000;

const SEVERITIES: readonly Severity[] = ['blocker', 'error', 'warning', 'info'];

/* "6 Nov 2026" — the day a binding was created, as the design writes it. */
const CREATED = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function createdOn(iso: string): string {
  const when = new Date(iso);
  return Number.isNaN(when.getTime()) ? iso.slice(0, 10) : CREATED.format(when);
}

export function BulkStarter({
  onAccepted,
}: {
  /** Called once the API has accepted a run, so the page can say so above the ledger it lands in. */
  readonly onAccepted: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();
  const headingId = useId();
  const ruleSelectId = useId();
  /* **Three states, not two, and the two-state version was a defect this screen shipped for one
     run.** `can` denies while the identity read is in flight and while it has failed, so a boolean
     gate cannot tell "your role does not carry this" from "we have not asked yet" — and the first
     reads as a claim about somebody's permissions made from a request that never answered. The
     notification bell records the same failure: it claimed *nothing is waiting for you* for the
     length of every identity read. Seen live here, on an expired token. */
  const standing: 'unknown' | 'forbidden' | 'permitted' = identity.isPending
    ? 'unknown'
    : identity.isError
      ? 'unknown'
      : can(identity.data, EDIT_BINDINGS_PERMISSION)
        ? 'permitted'
        : 'forbidden';
  const permitted = standing === 'permitted';

  const [open, setOpen] = useState(false);
  const [action, setAction] = useState<BulkAction>('disable');
  const [filters, setFilters] = useState<CandidateFilters>({
    rule: 'all',
    severity: 'all',
    pattern: '',
  });
  const [ruleQuery, setRuleQuery] = useState('');
  const [chosen, setChosen] = useState<readonly string[]>([]);
  const [chunk, setChunk] = useState(String(DEFAULT_CHUNK));

  /* The candidates a run of *this* action could be built from: a disable reads enabled bindings, an
     enable reads disabled ones. Switching the action therefore changes the population, which is
     why it clears the selection — carrying ids across would keep rows that are now all no-ops. */
  const wanted = action === 'enable' ? 'disabled' : 'enabled';
  const candidates = useBindingCandidates(wanted, open && permitted);
  const rules = useRules('', { enabled: open && permitted });
  const request = useRequestBulkRun();

  /* Memoised rather than `?? []` inline: a fresh empty array every render makes both memos below
     recompute on every render, which the lint rule is right to call out — and on a 500-row listing
     that is the filter and the selection join redone for nothing. */
  const all = useMemo(() => candidates.data ?? [], [candidates.data]);
  const matching = useMemo(
    () => all.filter((binding) => matchesFilters(binding, filters)),
    [all, filters],
  );

  /* **A rule's name, not its id.** The list is already fetched for the rule picker, so naming a
     candidate row costs nothing extra — the join is free, it was only never made. */
  const allRules = useMemo(() => rules.data?.items ?? [], [rules.data]);
  const rulesById = useMemo(
    () => new Map(allRules.map((rule) => [rule.id, rule])),
    [allRules],
  );
  /* The picker's options, narrowed by the search beside it. The chosen rule stays offered even when
     the search would hide it, so the select never shows a value its own list does not hold. */
  const ruleOptions = useMemo(() => {
    const needle = ruleQuery.trim().toLowerCase();
    return allRules.filter(
      (rule) =>
        needle === '' ||
        rule.name.toLowerCase().includes(needle) ||
        rule.id === filters.rule,
    );
  }, [allRules, ruleQuery, filters.rule]);

  /* The version *number* needs its own read — `rule_version_id` names nothing outside the versions
     list of the rule it belongs to. Bounded exactly as `DatasetDetail.tsx` bounds the identical
     join, and over distinct rules rather than distinct bindings: up to 500 candidate rows share a
     small number of rules in practice. */
  const distinctRuleIds = useMemo(
    () =>
      [...new Set(all.map((binding) => binding.rule_id))].slice(
        0,
        MAX_RULE_VERSION_LOOKUPS,
      ),
    [all],
  );
  const versionReads = useQueries({
    queries: distinctRuleIds.map((ruleId) => ({
      ...ruleVersionsQuery(ruleId),
      retry: false,
      staleTime: RULE_VERSION_STALE_MS,
    })),
  });
  const versionByVersionId = useMemo(() => {
    const found = new Map<string, string>();
    for (const result of versionReads) {
      for (const version of result.data?.items ?? []) {
        found.set(version.id, version.version);
      }
    }
    return found;
  }, [versionReads]);
  const chosenSet = useMemo(() => new Set(chosen), [chosen]);
  const selected = useMemo(
    () => all.filter((binding) => chosenSet.has(binding.id)),
    [all, chosenSet],
  );

  const facts = selectionFacts(selected, action);
  const tone = meterTone(facts);
  const block = submitBlock(facts, chunk);
  const chunks = chunkCount(facts, chunk);

  const reset = (): void => {
    setChosen([]);
    request.reset();
  };
  const changeFilters = (next: Partial<CandidateFilters>): void => {
    setFilters((current) => ({ ...current, ...next }));
    /* A filter change clears the selection rather than keeping off-screen ids: a submit carrying
       rows the current criteria do not show is a run over bindings nobody is looking at. */
    reset();
  };
  const toggleOpen = (): void => {
    setOpen(!open);
    reset();
  };

  if (standing === 'unknown') {
    /* Nothing, rather than either answer. The starter is one control on a page whose history is
       the point; an absent control for a moment is honest, and a wrong sentence about somebody's
       role is not. */
    return <></>;
  }

  if (standing === 'forbidden') {
    return (
      <p className="lq-tintband lq-tintband--muted">
        <span className="lq-tintband__text">
          <strong>{t('bulk.start.denied.title')}</strong> {t('bulk.start.denied.body')}
        </span>
      </p>
    );
  }

  return (
    <section className="lq-cp__card lq-bk__starter" aria-labelledby={headingId}>
      <div className="lq-bk__starterHead">
        <div className="lq-bk__starterText">
          <h2 id={headingId} className="lq-cp__cardTitle">
            {t('bulk.start.title')}
          </h2>
          <p className="lq-bk__starterBody">{t('bulk.start.body')}</p>
        </div>
        <button
          type="button"
          className="lq-cp__headButton"
          aria-expanded={open}
          onClick={toggleOpen}
        >
          {open ? t('bulk.start.close') : t('bulk.start.choose')}
        </button>
      </div>

      {open && (
        <>
          <div className="lq-bk__actionRow">
            <div
              className="lq-bk__segment"
              role="group"
              aria-label={t('bulk.start.change')}
            >
              {(['disable', 'enable'] as readonly BulkAction[]).map((each) => (
                <button
                  key={each}
                  type="button"
                  className="lq-bk__segmentButton"
                  aria-pressed={each === action}
                  onClick={() => {
                    setAction(each);
                    reset();
                  }}
                >
                  {t(`bulk.start.action.${each}`)}
                </button>
              ))}
            </div>
            <span className="lq-bk__actionNote">
              {t(`bulk.start.actionNote.${action}`)}
            </span>
          </div>

          <div className="lq-bk__criteria">
            <div className="lq-bk__field">
              <label htmlFor={ruleSelectId} className="lq-bk__fieldLabel">
                {t('bulk.start.rule')}
              </label>
              <span className="lq-bk__ruleControls">
                <input
                  type="search"
                  className="lq-bk__input"
                  value={ruleQuery}
                  placeholder={t('bulk.start.findRule')}
                  aria-label={t('bulk.start.findRule')}
                  onChange={(event) => {
                    setRuleQuery(event.target.value);
                  }}
                />
                <select
                  id={ruleSelectId}
                  className="lq-bk__input lq-bk__ruleSelect"
                  value={filters.rule}
                  onChange={(event) => {
                    changeFilters({ rule: event.target.value });
                  }}
                >
                  <option value="all">{t('bulk.start.everyRule')}</option>
                  {ruleOptions.map((rule) => (
                    <option key={rule.id} value={rule.id}>
                      {rule.name}
                    </option>
                  ))}
                </select>
              </span>
            </div>

            <label className="lq-bk__field">
              <span className="lq-bk__fieldLabel">{t('bulk.start.severity')}</span>
              <select
                className="lq-bk__input"
                value={filters.severity}
                onChange={(event) => {
                  changeFilters({ severity: event.target.value });
                }}
              >
                <option value="all">{t('bulk.start.anySeverity')}</option>
                {SEVERITIES.map((severity) => (
                  <option key={severity} value={severity}>
                    {t(`bulk.start.severity.${severity}`)}
                  </option>
                ))}
              </select>
            </label>

            <label className="lq-bk__field">
              <span className="lq-bk__fieldLabel">{t('bulk.start.pattern')}</span>
              <input
                className="lq-bk__input lq-mono"
                value={filters.pattern}
                placeholder="gold.*"
                onChange={(event) => {
                  changeFilters({ pattern: event.target.value });
                }}
              />
            </label>

            {/* The route caps at 500, which is below the 5,000 a run may carry — said always,
                beside the criteria that narrow it, rather than only once the cap bites. */}
            <span className="lq-bk__criteriaHint">
              {t('bulk.start.patternHint', { cap: CANDIDATE_PAGE })}
            </span>
          </div>

          {candidates.isPending && <SkeletonRows rows={5} columns={6} />}
          {candidates.isError && (
            <Alert tone="error" title={t('bulk.start.candidatesFailed')}>
              {candidates.error.message}
            </Alert>
          )}

          {candidates.isSuccess && (
            <>
              <div className="lq-bk__matchRow">
                <span>{t('bulk.start.matchLine', { count: matching.length })}</span>
                <button
                  type="button"
                  className="lq-cp__headButton"
                  disabled={matching.length === 0}
                  onClick={() => {
                    setChosen(matching.map((binding) => binding.id));
                    request.reset();
                  }}
                >
                  {t('bulk.start.selectAll', { count: matching.length })}
                </button>
                <button
                  type="button"
                  className="lq-cp__headButton"
                  disabled={chosen.length === 0}
                  onClick={reset}
                >
                  {t('bulk.start.clear')}
                </button>
                <span className={`lq-bk__meter lq-bk__meter--${tone}`}>
                  {t('bulk.start.meterLine', {
                    selected: facts.selected,
                    ceiling: SELECTION_CEILING,
                  })}
                </span>
              </div>

              {facts.overCeiling && (
                <Alert tone="error" title={t('bulk.start.over.title')}>
                  {t('bulk.start.over.body', {
                    selected: facts.selected,
                    ceiling: SELECTION_CEILING,
                  })}
                </Alert>
              )}

              {facts.selected > 0 && facts.willChange === 0 && (
                <Alert tone="warning" title={t(`bulk.start.allNoop.${action}`)}>
                  {t('bulk.start.allNoopBody', { count: facts.selected })}
                </Alert>
              )}

              <div className="lq-bk__candidates">
                <table className="lq-bk__candidateTable">
                  <caption className="lq-visually-hidden">
                    {t('bulk.start.caption')}
                  </caption>
                  <thead>
                    <tr>
                      <th className="lq-bk__pickCol">
                        <span className="lq-visually-hidden">
                          {t('bulk.start.column.pick')}
                        </span>
                      </th>
                      <th>{t('bulk.start.column.rule')}</th>
                      <th>{t('bulk.start.column.severity')}</th>
                      <th>{t('bulk.start.column.selector')}</th>
                      <th>{t('bulk.start.column.now')}</th>
                      <th>{t('bulk.start.column.created')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matching.map((binding) => (
                      <CandidateRow
                        key={binding.id}
                        binding={binding}
                        rule={rulesById.get(binding.rule_id) ?? null}
                        versionLabel={
                          versionByVersionId.get(binding.rule_version_id) ?? null
                        }
                        action={action}
                        checked={chosenSet.has(binding.id)}
                        onToggle={() => {
                          setChosen((current) =>
                            current.includes(binding.id)
                              ? current.filter((id) => id !== binding.id)
                              : [...current, binding.id],
                          );
                          request.reset();
                        }}
                      />
                    ))}
                  </tbody>
                </table>
                {/* Two different sentences. No candidate at all is a fact about the estate; no
                    match is a fact about the filter, and an empty result read as the second when
                    it is the first would send somebody widening criteria that cannot help. */}
                {matching.length === 0 ? (
                  <p className="lq-bk__none">
                    {all.length === 0
                      ? `${t(`bulk.start.noneAvailable.${action}`)}. ${t(
                          `bulk.start.noneAvailableBody.${action}`,
                        )}`
                      : t('bulk.start.noMatch.title')}
                  </p>
                ) : null}
              </div>

              <div className="lq-bk__stats">
                <div className="lq-bk__stat" title={t('bulk.start.stat.changeNote')}>
                  <div className="lq-bk__statKey">{t('bulk.start.stat.change')}</div>
                  <div className="lq-bk__statValue">{t(`bulk.action.${action}`)}</div>
                </div>
                <div className="lq-bk__stat" title={t('bulk.start.stat.willChangeNote')}>
                  <div className="lq-bk__statKey">{t('bulk.start.stat.willChange')}</div>
                  <div className="lq-bk__statValue">
                    {facts.willChange.toLocaleString()}
                  </div>
                </div>
                <div
                  className="lq-bk__stat"
                  title={t(`bulk.start.stat.willSkipNote.${action}`)}
                >
                  <div className="lq-bk__statKey">{t('bulk.start.stat.willSkip')}</div>
                  <div
                    className={
                      facts.willSkip > 0
                        ? 'lq-bk__statValue lq-bk__statValue--warn'
                        : 'lq-bk__statValue'
                    }
                  >
                    {facts.willSkip.toLocaleString()}
                  </div>
                </div>
                <div className="lq-bk__stat" title={identity.data?.subject}>
                  <div className="lq-bk__statKey">{t('bulk.start.stat.by')}</div>
                  {/* "You", as the design says it — the person reading is the one who would start
                      it — with the subject the journal records on hover. */}
                  <div className="lq-bk__statValue">{t('bulk.start.stat.byYou')}</div>
                </div>
              </div>

              <label className="lq-bk__chunkField">
                {t('bulk.start.chunk')}
                <input
                  className="lq-bk__input lq-bk__chunkInput"
                  value={chunk}
                  inputMode="numeric"
                  onChange={(event) => {
                    setChunk(event.target.value);
                  }}
                />
                {chunks === null ? null : (
                  <span className="lq-bk__chunkCount">
                    {t('bulk.start.chunks', { count: chunks })}
                  </span>
                )}
              </label>

              <p className="lq-bk__line">
                {t('bulk.start.confirmLine', {
                  selected: facts.selected,
                  change: facts.willChange,
                  skip: facts.willSkip,
                })}
              </p>

              {request.isError && (
                <Alert tone="error" title={t('bulk.start.failed')}>
                  {request.error.message}
                </Alert>
              )}

              <div className="lq-bk__submitRow">
                <button type="button" className="lq-bk__cancel" onClick={toggleOpen}>
                  {t('bulk.start.cancel')}
                </button>
                <button
                  type="button"
                  className="lq-bk__submit"
                  disabled={block !== 'none' || request.isPending}
                  onClick={() => {
                    request.mutate(
                      {
                        action: actionEvent(action),
                        bindingIds: chosen,
                        chunkSize: Number(chunk.trim()),
                      },
                      {
                        onSuccess: () => {
                          /* Cleared and closed on success: hard rule 7's key stops a retry
                             becoming a second run, and clearing stops a deliberate re-submit
                             without having re-chosen. */
                          setChosen([]);
                          setOpen(false);
                          onAccepted();
                        },
                      },
                    );
                  }}
                >
                  {t(`bulk.start.submit.${action}`, { count: facts.selected })}
                </button>
                <span className="lq-bk__submitHint">
                  {t(`bulk.start.block.${block}`)}
                </span>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}

function CandidateRow({
  binding,
  rule,
  versionLabel,
  action,
  checked,
  onToggle,
}: {
  readonly binding: RuleBinding;
  /** `null` while the rules listing has not answered yet, or the rule no longer exists — the
   * second is a real possibility a picker over bindings must survive, since a binding can outlive
   * the rule it names if the rule is later retired. */
  readonly rule: Rule | null;
  /** `null` while unresolved, past the lookup's bound, or the version has been superseded and no
   * longer appears in the rule's own version list. */
  readonly versionLabel: string | null;
  readonly action: BulkAction;
  readonly checked: boolean;
  readonly onToggle: () => void;
}): ReactElement {
  const { t } = useTranslation();
  /* Every candidate would change by construction — the listing is filtered to the opposite state —
     but the row says so when it would not, because a filter changed elsewhere or a binding toggled
     while the picker is open makes that assumption false, and a row silently promising a change it
     would skip is the one thing the figures under the table exist to prevent. */
  const noop = binding.enabled === (action === 'enable');

  return (
    <tr className="lq-bk__candidate" onClick={onToggle}>
      <td className="lq-bk__pickCol">
        <input
          type="checkbox"
          className="lq-bk__pick"
          checked={checked}
          aria-label={t('bulk.start.pickRow', { selector: selectorText(binding) })}
          onChange={onToggle}
          onClick={(event) => {
            /* The row's own handler already toggles; without this the click bubbles and toggles
               twice, which reads as the checkbox not working. */
            event.stopPropagation();
          }}
        />
      </td>
      <td title={rule === null ? binding.rule_id : t(`rules.kind.${rule.kind}`)}>
        {rule === null ? (
          /* The one place an id is still behind a label, and only because there is genuinely no
             name to show — a binding whose rule was retired after it was bound. */
          <span className="lq-muted">{t('bulk.start.ruleGone')}</span>
        ) : (
          rule.name
        )}
        {versionLabel === null ? null : (
          <span className="lq-bk__version"> v{versionLabel}</span>
        )}
      </td>
      <td>{t(`bulk.start.severity.${binding.severity}`)}</td>
      <td className="lq-bk__selector">{selectorText(binding)}</td>
      <td className="lq-bk__now">
        {t(binding.enabled ? 'bulk.start.now.enabled' : 'bulk.start.now.disabled')}
        {noop ? (
          <span className="lq-bk__skip"> · {t('bulk.start.effect.skip')}</span>
        ) : null}
      </td>
      <td className="lq-bk__created" title={binding.created_by}>
        {createdOn(binding.created_at)}
      </td>
    </tr>
  );
}

/** The binding's selectors as one readable string.
 *
 * Joined rather than showing the first: selectors are a *union*, so a binding with two of them
 * covers both and rendering one would understate what a run touches.
 */
function selectorText(binding: RuleBinding): string {
  const parts = binding.selectors
    .map((selector) => selector.pattern ?? selector.tag ?? selector.role ?? '')
    .filter((text) => text !== '');
  return parts.length === 0 ? '—' : parts.join(', ');
}
